/* eslint-disable no-undef */
(function () {
  'use strict';

  window.OletLens = window.OletLens || {};

  var Config = window.OletLens.Config || {};

  function generateBundleKey(frameVariantId) {
    var ts = Date.now();
    var random = Math.random().toString(36).slice(2, 6);
    return frameVariantId + '_' + ts + '_' + random;
  }

  function getMaxCyl(odCyl, osCyl) {
    var od = Math.abs(Number(odCyl) || 0);
    var os = Math.abs(Number(osCyl) || 0);
    return Math.max(od, os);
  }

  function shouldApplyHighCylSurcharge(maxCyl) {
    var threshold = 2.2;
    return maxCyl > threshold;
  }

  function getRecommendedLensTier(odSph, odCyl, osSph, osCyl) {
    var absValues = [
      Math.abs(Number(odSph) || 0),
      Math.abs(Number(odCyl) || 0),
      Math.abs(Number(osSph) || 0),
      Math.abs(Number(osCyl) || 0),
    ];
    var maxValue = Math.max.apply(null, absValues);
    var rec = Config.LENS_RECOMMENDATIONS || {};

    if (maxValue <= (rec.BASIC_MAX || 2)) return 'basic';
    if (maxValue <= (rec.STANDARD_MAX || 3)) return 'standard';
    if (maxValue <= (rec.PREMIUM_MAX || 6)) return 'premium';
    return 'elite';
  }

  function resolveVariantId(usageType, tier) {
    var map = Config.LENS_BASE_VARIANTS || {};
    var byUsage = map[usageType];
    if (!byUsage) return null;
    return byUsage[tier] || null;
  }

  function resolveAddonVariantId(addonType) {
    var map = Config.ADDON_VARIANTS || {};
    return map[addonType] || null;
  }

  function normalizeSunglassesAddonKey(key) {
    if (!key) return key;
    if (key === 'photochromic_polarized_sunglasses') return 'photochromic_polarized';
    return key;
  }

  /**
   * Add-on keys that must not appear in cart/order for this lens context.
   * Rules: Polycarbonate base → all sun upgrades; Bifocal usage → polarized + photochromic polarized.
   */
  function getDisallowedAddonKeys(usageType, lensTier, lensBaseVariantId) {
    var disallowed = [];
    var rules = Config.ADDON_RULES || {};
    var polyIds = Config.POLYCARBONATE_LENS_VARIANT_IDS || [];
    var vid = lensBaseVariantId != null ? Number(lensBaseVariantId) : null;

    var isPoly =
      lensTier === 'polycarbonate' ||
      (vid != null && !Number.isNaN(vid) && polyIds.indexOf(vid) !== -1);

    function addKeys(keys) {
      (keys || []).forEach(function (k) {
        if (disallowed.indexOf(k) === -1) {
          disallowed.push(k);
        }
      });
    }

    if (isPoly) {
      addKeys(rules.sun_color_exclusive);
    }
    if (usageType === 'bifocals') {
      addKeys(rules.bifocal_incompatible);
    }

    return disallowed;
  }

  function isAddonKeyAllowedForLens(addonKey, usageType, lensTier, lensBaseVariantId) {
    var normalized = normalizeSunglassesAddonKey(addonKey);
    return getDisallowedAddonKeys(usageType, lensTier, lensBaseVariantId).indexOf(normalized) === -1;
  }

  function isAddonCompatible(addonType, usageType) {
    if (!addonType || !usageType) return true;
    return isAddonKeyAllowedForLens(addonType, usageType, null, null);
  }

  function buildRxJson(formData) {
    if (!formData) return {};
    if (formData.reading_strength) {
      return {
        person_name: formData.person_name || '',
        reading_strength: formData.reading_strength,
        pd: formData.pd || {},
      };
    }
    if (formData.non_prescription) return {};
    return {
      person_name: formData.person_name || '',
      od: formData.od || {},
      os: formData.os || {},
      pd: formData.pd || {},
      comments: formData.comments || '',
    };
  }

  function parseRxJson(jsonString) {
    if (!jsonString) return {};
    try {
      var parsed = JSON.parse(jsonString);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function validatePrescription(data) {
    var errors = [];
    if (!data) {
      errors.push('Missing prescription data');
      return { valid: false, errors: errors };
    }
    if (!data.person_name) errors.push('person_name_required');
    if (!data.od || typeof data.od !== 'object') errors.push('od_missing');
    if (!data.os || typeof data.os !== 'object') errors.push('os_missing');
    var pdResult = validatePD(data.pd, !!(data.pd && (data.pd.left || data.pd.right)));
    if (!pdResult.valid) errors.push.apply(errors, pdResult.errors);
    return { valid: errors.length === 0, errors: errors };
  }

  function validatePD(pd, isSeparate) {
    var errors = [];
    if (!pd) {
      errors.push('pd_missing');
      return { valid: false, errors: errors };
    }
    if (isSeparate) {
      if (!pd.left || !pd.right) errors.push('pd_left_right_required');
    } else if (!pd.combined) {
      errors.push('pd_combined_required');
    }
    return { valid: errors.length === 0, errors: errors };
  }

  function validatePrism(prism) {
    var errors = [];
    if (!prism || !prism.has_prism) return { valid: true, errors: errors };
    var hasVertical = prism.vertical != null;
    var hasHorizontal = prism.horizontal != null;
    if (!hasVertical && !hasHorizontal) errors.push('prism_value_required');
    return { valid: errors.length === 0, errors: errors };
  }

  function validateReadingForm(data) {
    var errors = [];
    if (!data) {
      errors.push('missing_reading_data');
      return { valid: false, errors: errors };
    }
    if (!data.strength) errors.push('Please select a reading strength.');
    var pdResult = validatePD(data.pd, !!(data.pd && (data.pd.left || data.pd.right)));
    if (!pdResult.valid) errors.push.apply(errors, pdResult.errors);
    return { valid: errors.length === 0, errors: errors };
  }

  window.OletLens.Utils = {
    generateBundleKey: generateBundleKey,
    buildRxJson: buildRxJson,
    parseRxJson: parseRxJson,
    validatePrescription: validatePrescription,
    validatePD: validatePD,
    validatePrism: validatePrism,
    validateReadingForm: validateReadingForm,
    isAddonCompatible: isAddonCompatible,
    normalizeSunglassesAddonKey: normalizeSunglassesAddonKey,
    getDisallowedAddonKeys: getDisallowedAddonKeys,
    isAddonKeyAllowedForLens: isAddonKeyAllowedForLens,
    getRecommendedLensTier: getRecommendedLensTier,
    resolveVariantId: resolveVariantId,
    resolveAddonVariantId: resolveAddonVariantId,
    getMaxCyl: getMaxCyl,
    shouldApplyHighCylSurcharge: shouldApplyHighCylSurcharge,
  };
})();
